import { createStore } from 'zustand/vanilla';
import { GameState, HeroData } from '../helpers/types';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

export type Store = {
    username: string;
    userRef: string;
    balance: number;
    bet: number;
    multiplier: number;
    currency: string;
    bets: number[];
    netWin: number;
    betPlaced: boolean;
    placingBet: boolean;
    selectedRacer: string;
    betOnRacer: string;
    raceId: string;
    gameState: GameState;
    activeHeroes: HeroData[];
    stateTick: number;
    betProgress: number | null;
    racerPanelShakeTick: number;
    socialLayer: {
        token: string;
        url: string;
    };
    racersBetCounts: Record<string, number>;

    setUser: (userRef: string, username: string) => void;
    setBalanceAndCurrency: (balance: number, currency: string) => void;
    setBets: (bets: number[]) => void;
    setBet: (bet: number) => void;
    setMultiplier: (multiplier: number) => void;
    selectRacer: (racerID: string) => void;
    setGameState: (gameState: GameState) => void;
    setActiveHeroes: (heroes: HeroData[]) => void;
    updateNetWin: (change: number) => void;
    setBetOnRacer: (betOnRacer: string) => void;
    setStateTick: (tick: number) => void;
    triggerRacerPanelShake: () => void;
    setRaceID: (raceId: string) => void;
    setBetProgress: (progress: number) => void;
    setSocials: (token: string, url: string) => void;
    incrementRacerBet: (racerId: string) => void;
    resetRound: () => void;
    getSelectedRacerDetails: () => HeroData | undefined;
};

export const store = createStore<Store>()(
    devtools(
        subscribeWithSelector((set, get) => ({
            username: '',
            userRef: '',
            balance: 100,
            bet: 0,
            multiplier: 1,
            currency: 'GBP',
            bets: [],
            raceId: '',
            selectedRacer: '',
            betOnRacer: '',
            gameState: 'INIT',
            activeHeroes: [],
            netWin: 0,
            betPlaced: false,
            placingBet: false,
            stateTick: 0,
            betProgress: null,
            racerPanelShakeTick: 0,
            emojis: [],
            socialLayer: {
                token: '',
                url: '',
            },
            racersBetCounts: {},
            setUser: (userRef, username) => set({ userRef, username }),
            setBalanceAndCurrency: (balance, currency) => set({ balance, currency }),
            setBet: (bet) => set({ bet }),
            setMultiplier: (multiplier) => set({ multiplier }),
            setBets: (bets) => set({ bets }),
            setRaceID: (raceId) => set({ raceId }),
            selectRacer: (selectedRacer) => set({ selectedRacer }),
            setBetOnRacer: (betOnRacer) =>
                set({ betOnRacer, selectedRacer: betOnRacer, betPlaced: true, placingBet: false }),
            setGameState: (gameState) => set({ gameState }),
            setActiveHeroes: (heroes: HeroData[]) => set({ activeHeroes: heroes }),
            updateNetWin: (change) => {
                set((state) => ({ netWin: state.netWin + change }));
            },
            setStateTick: (tick) => set({ stateTick: tick }),
            triggerRacerPanelShake: () =>
                set((state) => ({ racerPanelShakeTick: state.racerPanelShakeTick + 1 })),
            setBetProgress: (progress) => set({ betProgress: progress }),
            setSocials: (token: string, url: string) => set({ socialLayer: { token, url } }),
            incrementRacerBet: (racerId) =>
                set((state) => ({
                    racersBetCounts: {
                        ...state.racersBetCounts,
                        [racerId]: (state.racersBetCounts[racerId] ?? 0) + 1,
                    },
                })),
            resetRound: () => {
                set({
                    selectedRacer: '',
                    betOnRacer: '',
                    betPlaced: false,
                    placingBet: false,
                    betProgress: 0,
                    racerPanelShakeTick: 0,
                    racersBetCounts: {},
                });
            },
            getSelectedRacerDetails: () => {
                const selectedRacer = get().selectedRacer;
                return get().activeHeroes.find((hero) => hero.name === selectedRacer);
            },
        })),
        {
            name: 'main-store',
        },
    ),
);
